import type { ZoneCount } from "@tdelivr/api-client/generated/dispatch/zone-supply-read-model.js";
import type { SurgeOverride } from "@tdelivr/api-client/generated/pricing/dynamic-pricing.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { at, field, absent, optional, type RecordDetail } from "./record-detail.ts";

/**
 * What the surge board does to the three readings behind it, away from any rendering.
 *
 * **This was the last unbuilt screen on the platform, and the reason was its middle column.**
 * `Demand / supply` wanted a count per zone at the rate a board refreshes, and
 * `docs/design/UNBUILT_SCREENS.md` measured five services against it on 22 October: the driver
 * availability service models no zone, driver location publishes positions with deliberately no
 * zone, the service-area service evaluates one point per call, and the analytics warehouse buckets
 * by day. DSP-06 was built for this column and nothing else.
 *
 * ## Three readings, and why it takes three
 *
 * **GEO-06 decides which rows exist.** It owns the zones, so the board's row set is its active
 * areas. DSP-06 deliberately omits a zone it holds no row for — reporting an empty one would be a
 * claim about a zone it cannot see — so a quiet zone arrives here as a missing key and is drawn
 * `0 / 0`, which is what the design's `Balanced 29` counts.
 *
 * **DSP-06 fills the counts.** Demand outstanding, supply fresh and available, and separately the
 * supply whose position has gone stale, which it refuses to fold into zero.
 *
 * **PRC-03 answers the override half.** A zone-scoped manual override with its reason, its
 * two-hour bound and its early ending.
 *
 * ## The one number this board cannot read, and does not guess
 *
 * The `Surge zones` tile wants every zone where a multiplier above 1.0 is in force. **Automatic
 * surge is not a stored fact on this platform**: PRC-03's `evaluate` is handed a demand count and a
 * supply count at quote time and returns an adjustment it keeps nothing of, and the policy it
 * evaluates is addressable only by a `policyId` no operation lists. So the knowable half of that
 * tile is the manual overrides, which is a floor rather than a count — and drawing a floor as a
 * count is exactly the relabelling Q-238 was recorded to prevent. The tile carries the absence and
 * says what would have to be published. Q-246.
 *
 * The `Surge` column follows from the same fact: a zone with a live override shows its multiplier,
 * and a zone without one shows the absence rather than the `1.0×` that would claim surge had been
 * checked and found off.
 */

/** A zone as the board draws it: GEO-06's identity, DSP-06's counts, PRC-03's override. */
export interface SurgeZoneRow {
  readonly zoneId: string;
  /** GEO-06's name for the area, which is the design's `Westlands`. */
  readonly name: string;
  readonly demand: number;
  /** Available and reporting. Never includes `stale`. */
  readonly supply: number;
  /** Available and not reporting, kept apart because zero supply and unknown supply differ. */
  readonly stale: number;
  readonly onJob: number;
  /** DSP-06's own classification, or `balanced` for a zone it holds no row for. */
  readonly standing: SurgeStanding;
  /** The live override, where one is in force. */
  readonly override?: SurgeOverride;
}

export type SurgeStanding = "undersupplied" | "stretched" | "balanced";

/**
 * What the board needs from a service area, which is its identity and its name.
 *
 * Narrower than GEO-06's `ServiceArea` on purpose. That type carries a boundary, a market, product
 * references, a priority, a lifecycle, two effective dates and three audit fields, and this board
 * reads none of them — the lifecycle is spent on the request rather than on the row. Taking the
 * whole type would make every test fixture carry nine fields no assertion touches, and would make
 * a change to any of them a change to this module's surface.
 */
export interface Zone {
  readonly id: string;
  readonly name: string;
}

/** DSP-06's row, as the client types it, including the standing the service computes. */
export interface ZoneSupplyRow extends ZoneCount {
  readonly standing: SurgeStanding;
}

/**
 * Whether an override is in force at this instant.
 *
 * Both halves matter and the service enforces both: an override ends when somebody ends it, and
 * otherwise at its own expiry. A board reading only `endedAt` would keep showing an expired
 * override as live, which on this screen means an operator believing a surge is on.
 */
export function isLive(override: SurgeOverride, at: number): boolean {
  if (override.endedAt !== undefined) return false;
  return Date.parse(override.expiresAt) > at;
}

/**
 * The multiplier as the design writes it: `1.4×`.
 *
 * Basis points are the wire form because a multiplier is money arithmetic and a float is not
 * exact. One decimal place is the design's own precision on every row and in every select option.
 */
export function multiplier(basisPoints: number): string {
  return `${(basisPoints / 10000).toFixed(1)}×`;
}

/** `13:30`, the design's own form for an expiry on a row and in a badge. */
export function clock(instant: string): string {
  const parsed = Date.parse(instant);
  if (Number.isNaN(parsed)) return instant;
  return new Date(parsed).toISOString().slice(11, 16);
}

/**
 * The board's rows: GEO-06's active areas, filled from DSP-06 and PRC-03.
 *
 * Sorted by what needs attention first — the design's own ordering intent, with an override at the
 * top because it is the one row a person put there, then undersupplied, then stretched, then the
 * quiet zones alphabetically so the list does not reshuffle under a reader between refreshes.
 */
export function zoneRows(
  areas: readonly Zone[],
  counts: readonly ZoneSupplyRow[],
  overrides: readonly SurgeOverride[],
  now: number,
): readonly SurgeZoneRow[] {
  const byZone = new Map(counts.map((count) => [count.zoneId, count]));
  /*
   * Newest first is how PRC-03 answers, and the first live one found is therefore the current one.
   * The service holds an advisory lock so a second live override on one zone is unrepresentable,
   * but the board does not depend on that being true -- it takes the newest and ignores the rest.
   */
  const live = new Map<string, SurgeOverride>();
  for (const override of overrides) {
    if (!isLive(override, now)) continue;
    if (!live.has(override.zoneId)) live.set(override.zoneId, override);
  }

  const rows = areas.map((area): SurgeZoneRow => {
    const count = byZone.get(area.id);
    const override = live.get(area.id);
    return {
      zoneId: area.id,
      name: area.name,
      demand: count?.demand ?? 0,
      supply: count?.supply ?? 0,
      stale: count?.stale ?? 0,
      onJob: count?.onJob ?? 0,
      /*
       * A zone DSP-06 holds no row for is quiet, not unknown: nothing is waiting and nobody is
       * there, which is `balanced` by the service's own classification and the design's `Balanced`
       * tile. The service is asked rather than reclassified here, so the board and PRC-03 cannot
       * disagree about one zone.
       */
      standing: count?.standing ?? "balanced",
      ...(override === undefined ? {} : { override }),
    };
  });

  /*
   * In GEO-06's order, which is the design's.
   *
   * This re-ranked by what needs attention, and `check-surge.mts` found the design's own table in
   * a different order: Westlands, CBD, Ruaka, JKIA, which is the order the areas come in. The rule
   * in `CLAUDE.md` is the same order as the design, and the service's order is also the one a
   * tenant chose — `priority` is a field on a service area. Needs-attention-first is still
   * offered, as a sort an operator picks.
   */
  return rows;
}

/**
 * Where a row sorts when the operator asks for what needs attention first.
 *
 * Override, then undersupplied, then stretched, then the quiet zones. An override sorts above a
 * shortage because it is the one a person put there and the one somebody may be about to be asked
 * to end.
 */
function attentionRank(row: SurgeZoneRow): number {
  if (row.override !== undefined) return 0;
  if (row.standing === "undersupplied") return 1;
  if (row.standing === "stretched") return 2;
  return 3;
}

/**
 * The design's four tiles. `surgeZones` is `undefined` where it cannot be known, never zero.
 */
export interface SurgeTiles {
  /**
   * Zones where a multiplier above 1.0 is in force.
   *
   * `undefined` always, until automatic surge is a readable fact per zone. The manual overrides
   * are a floor on this number, not the number.
   */
  readonly surgeZones: undefined;
  readonly balanced: number;
  readonly undersupplied: number;
  readonly manualOverrides: number;
  /** The design's `expires 13:30`: the soonest expiry among the live overrides. */
  readonly soonestExpiry: string | undefined;
  /** The design's `max 1.4×`, over the overrides, which is the half that is knowable. */
  readonly highestManualMultiplier: number | undefined;
}

export function tilesFor(rows: readonly SurgeZoneRow[]): SurgeTiles {
  const live = rows.flatMap((row) => (row.override === undefined ? [] : [row.override]));
  const expiries = live.map((override) => override.expiresAt).sort();
  const multipliers = live.map((override) => override.multiplierBasisPoints);

  return {
    surgeZones: undefined,
    /*
     * A stretched zone is counted in neither tile, which is the design's own arithmetic: its four
     * tiles read `2 · 29 · 3 · 1` over thirty-four zones, so `Balanced` is the matched ones and not
     * "everything that is not undersupplied".
     */
    balanced: rows.filter((row) => row.standing === "balanced").length,
    undersupplied: rows.filter((row) => row.standing === "undersupplied").length,
    manualOverrides: live.length,
    soonestExpiry: expiries[0],
    highestManualMultiplier: multipliers.length === 0 ? undefined : Math.max(...multipliers),
  };
}

/**
 * The design's `Demand / supply` cell, and what the title has to carry.
 *
 * Two numbers because the design has two columns' worth of room and one of them is this. The stale
 * and on-job figures go in the title rather than into either number: a stale driver folded into
 * `supply` is a driver an operator would dispatch against, and folded into zero is a shortage that
 * is really a connectivity problem.
 */
export function supplyCell(row: SurgeZoneRow): { readonly text: string; readonly title: string } {
  const parts = [
    `${String(row.demand)} waiting, offering or assigned.`,
    `${String(row.supply)} available and reporting a position under two minutes old.`,
  ];
  if (row.stale > 0) {
    parts.push(
      `${String(row.stale)} available but not reporting — counted apart from supply, because a zone with no drivers and a zone whose drivers stopped reporting are different problems.`,
    );
  }
  if (row.onJob > 0) {
    /*
     * "Online, not taking offers" rather than "on a job", because that is what the number counts.
     * Nothing published says which driver took which job — the assignment and completion events
     * carry a demand and an offer and no driver — so DSP-06 reaches `on_job` from DRV-05's
     * "on shift and unavailable", which also catches a driver who has just signed on. Q-248.
     */
    parts.push(`${String(row.onJob)} online and not taking offers.`);
  }

  return { text: `${String(row.demand)} / ${String(row.supply)}`, title: parts.join(" ") };
}

/**
 * The design's `Surge` column: `1.2× manual`, or the absence.
 *
 * The design draws `1.4×` for a stretched zone with automatic surge on and `1.0×` for a balanced
 * one. Neither is readable: nothing stores a zone's automatic multiplier, and the policy it would
 * be computed from is addressable only by a `policyId` no operation lists. `1.0×` here would say
 * surge had been checked and found off.
 */
export function surgeCell(row: SurgeZoneRow): {
  readonly text: string;
  readonly title: string;
  readonly absent: boolean;
} {
  if (row.override !== undefined) {
    return {
      text: `${multiplier(row.override.multiplierBasisPoints)} manual`,
      title: `Raised by hand, expiring ${clock(row.override.expiresAt)}. Reason: ${row.override.reason}`,
      absent: false,
    };
  }
  return {
    text: "—",
    title:
      "No manual override. The automatic multiplier is not readable: PRC-03 computes an adjustment per quote from a demand and supply count and stores none of it, and the policy behind it is addressable only by a policyId no operation lists. 1.0× here would claim surge had been checked and found off (Q-246).",
    absent: true,
  };
}

/**
 * The row's badge, in the design's own four words.
 *
 * `surge` describes the supply condition — more demand than supply, which is where surge is asked
 * for — and not a multiplier read from PRC-03. The `Surge` column is where that absence is drawn,
 * so the word and the number say different things in the places the design put them.
 *
 * A zone whose supply is entirely stale classifies `undersupplied` and so reads `no supply`. That
 * is DSP-06's own classification and the design's own word; whether this board should distinguish
 * it is Q-247, and meanwhile the staleness is in the `Demand / supply` title.
 */
export function badgeFor(row: SurgeZoneRow): {
  readonly classKey: StateClassKey;
  readonly label: string;
  /** The contract's own word for the state, which is what `StateBadge` checks against. */
  readonly status: string;
  /** The service that owns the state, never the one that owns the row. */
  readonly code: string;
} {
  if (row.override !== undefined) {
    return {
      classKey: "human",
      label: `override · ${clock(row.override.expiresAt)}`,
      status: "override",
      code: "PRC-03",
    };
  }
  if (row.standing === "undersupplied") {
    return { classKey: "failed", label: "no supply", status: "undersupplied", code: "DSP-06" };
  }
  if (row.standing === "stretched") {
    return { classKey: "caution", label: "surge", status: "stretched", code: "DSP-06" };
  }
  return { classKey: "done", label: "balanced", status: "balanced", code: "DSP-06" };
}

/** The design's multiplier select, in its own words and order. */
export const MULTIPLIER_OPTIONS: readonly {
  readonly label: string;
  readonly basisPoints: number;
}[] = [
  { label: "1.0× (off)", basisPoints: 10000 },
  { label: "1.2×", basisPoints: 12000 },
  { label: "1.4×", basisPoints: 14000 },
  { label: "1.6×", basisPoints: 16000 },
];

/** The form's `Max 2 hours`, which PRC-03 enforces as a CHECK rather than trusting the caller. */
export const MAXIMUM_OVERRIDE_MINUTES = 120;

/**
 * Whether an expiry the operator typed is one PRC-03 will accept.
 *
 * Checked here as well as there so the form can say which rule it broke. The service refuses it
 * either way — the bound is a named constraint on the table — and this is the sentence rather than
 * a 400 the operator has to interpret.
 */
export function expiryProblem(expiresAt: string, now: number): string | undefined {
  const parsed = Date.parse(expiresAt);
  if (Number.isNaN(parsed)) return "Give a date and time for this override to expire.";
  if (parsed <= now) return "An override has to expire in the future.";
  if (parsed - now > MAXIMUM_OVERRIDE_MINUTES * 60 * 1000) {
    return `Max ${String(MAXIMUM_OVERRIDE_MINUTES / 60)} hours. An override that outlasts a shift is a rate change nobody approved.`;
  }
  return undefined;
}

/** One zone's detail panel. */
export function zoneDetail(row: SurgeZoneRow): RecordDetail {
  const badge = badgeFor(row);
  const surge = surgeCell(row);
  const override = row.override;

  return {
    headline: row.name,
    identifier: row.zoneId,
    source: override === undefined ? "DSP-06 · GEO-06" : "DSP-06 · GEO-06 · PRC-03",
    state: { classKey: badge.classKey, label: badge.label, code: badge.code, status: badge.status },
    fields: [
      field("Demand outstanding", String(row.demand)),
      field("Available and reporting", String(row.supply)),
      field("Available, not reporting", String(row.stale)),
      field("Online, not taking offers", String(row.onJob)),
      surge.absent
        ? absent("Surge in effect", surge.title)
        : field("Surge in effect", surge.text, true),
      ...(override === undefined
        ? []
        : [
            field("Override", override.overrideId, true),
            field("Raised by", override.createdBy, true),
            field("Raised", at(override.createdAt) ?? override.createdAt),
            field("Expires", at(override.expiresAt) ?? override.expiresAt),
            field("Reason", override.reason),
            ...optional("Ended", at(override.endedAt)),
            ...optional("Ended by", override.endedBy, true),
          ]),
    ],
    note:
      override === undefined
        ? "Demand and supply are counted from events as drivers move and requests arrive, not asked of each service when this board refreshes."
        : "Manual surge overrides need a reason and expire on their own.",
  };
}

/* ------------------------------------------------------------------------- *
 * The data-grid toolbar.
 *
 * Design rule 4: every grid has search, filter, sort, pagination with rows-per-page and export to
 * CSV, Excel and PDF, plus an empty state with a Clear filters button. `CLAUDE.md` names the
 * toolbar among the things a build may not drop.
 *
 * Every one of those works over the joined rows this board already holds rather than over a query
 * parameter, and that is a property of the three reads rather than a shortcut: DSP-06 answers every
 * zone in one read with no required query, PRC-03's listing takes only a cursor, and GEO-06's takes
 * a lifecycle. There is no server-side search, filter or sort to delegate to — and the set is
 * bounded by how many zones a tenant operates, which is tens, so paging it in the browser is the
 * whole estate rather than a window onto it. An export therefore writes every matching row and not
 * the page on screen.
 * ------------------------------------------------------------------------- */

export type SurgeOrder = "default" | "attention" | "demand" | "az" | "za";

export interface SurgeQuery {
  /** Free text over every value on the row, as the design's grid searches. */
  readonly search: string;
  /** One standing, or `undefined` for all of them. */
  readonly standing?: SurgeStanding | "override";
  readonly order: SurgeOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
}

export interface SurgePage {
  /** The rows on the current page. */
  readonly rows: readonly SurgeZoneRow[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly SurgeZoneRow[];
  readonly total: number;
  readonly pageCount: number;
  /** Whether any filter is set, which decides whether the empty state offers Clear filters. */
  readonly filtered: boolean;
}

/** Every word on a row a search should match: the zone, its standing, and an override's reason. */
function searchableText(row: SurgeZoneRow): string {
  return [
    row.name,
    row.zoneId,
    row.standing,
    badgeFor(row).label,
    row.override?.reason ?? "",
    row.override === undefined ? "" : multiplier(row.override.multiplierBasisPoints),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the board holds.
 *
 * `default` is GEO-06's own order, which is the design's and is also the order a tenant chose:
 * `priority` is a field on a service area. A board that silently re-sorted it would be making an
 * editorial decision about somebody else's estate on every refresh.
 */
export function surgePage(rows: readonly SurgeZoneRow[], query: SurgeQuery): SurgePage {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter((row) => {
    if (needle.length > 0 && !searchableText(row).includes(needle)) return false;
    if (query.standing === undefined) return true;
    if (query.standing === "override") return row.override !== undefined;
    return row.standing === query.standing;
  });

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "attention") {
            const byRank = attentionRank(left) - attentionRank(right);
            return byRank !== 0 ? byRank : left.name.localeCompare(right.name);
          }
          // Most demand first, which is the one numeric order this board has a use for.
          if (query.order === "demand") return right.demand - left.demand;
          const compared = left.name.localeCompare(right.name);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return {
    rows: ordered.slice(from, from + query.perPage),
    all: ordered,
    total,
    pageCount,
    filtered: needle.length > 0 || query.standing !== undefined,
  };
}

/** The filter's choices, in the words the badges use so the two cannot disagree. */
export const STANDING_FILTERS: readonly {
  readonly value: SurgeStanding | "override";
  readonly label: string;
}[] = [
  { value: "undersupplied", label: "no supply" },
  { value: "stretched", label: "surge" },
  { value: "balanced", label: "balanced" },
  { value: "override", label: "manual override" },
];

/** The sort choices, in the design's own order of usefulness on this board. */
export const ORDER_CHOICES: readonly { readonly value: SurgeOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "attention", label: "Needs attention first" },
  { value: "demand", label: "Most demand" },
  { value: "az", label: "Zone A–Z" },
  { value: "za", label: "Zone Z–A" },
];

/** The design's own rows-per-page choices, in its order. */
export const PER_PAGE_CHOICES: readonly number[] = [10, 25, 50];

/**
 * The export's rows, as the three columns the board draws.
 *
 * The `Surge` cell exports the same `—` the screen shows, deliberately: a spreadsheet is where a
 * figure gets quoted to somebody who was not looking at the board, and an empty cell there reads
 * as "no surge" rather than as "not knowable".
 */
export function exportRow(row: SurgeZoneRow): readonly string[] {
  return [row.name, supplyCell(row).text, surgeCell(row).text, badgeFor(row).label];
}

export const EXPORT_HEADER: readonly string[] = ["Zone", "Demand / supply", "Surge", "State"];

/**
 * The design's four tiles, as the tuples it declares them with.
 *
 * `19 WP18 Dispatch Operations` writes each tile as `[label, value, class, note]` and draws the
 * third line as `badge(class, note || "—")` — so the subtitle is a state badge and not plain text,
 * and the card takes a 1.5px ring in the class's own colour when that class is `failed` or
 * `human`. The class belongs to the tile's subject rather than to its count, which is why
 * `Undersupplied` keeps its ring at zero: the design's tuples are fixed.
 *
 * One departure, and it is the `Surge zones` tile. The design gives it `caution`, which says
 * surge is on somewhere. This board cannot know that — automatic surge is recomputed per quote and
 * stored nowhere — so the tile takes `unknown`, the design's own dashed class for a value nobody
 * can supply, and its note says what is knowable instead. Q-246.
 */
export interface SurgeTile {
  readonly label: string;
  /** `undefined` where the figure cannot be known, drawn as the design's dash. */
  readonly value: number | undefined;
  readonly classKey: StateClassKey;
  /** The badge's word. The design's own fallback for an empty note is a dash. */
  readonly note: string;
  /** The service that owns the figure, for the badge's own state lookup. */
  readonly code: string;
  readonly status: string;
  /** Why the value is absent, where it is. */
  readonly title?: string;
}

const SURGE_ZONES_ABSENT =
  "Every zone where a multiplier above 1.0 is in force, which this platform does not store. PRC-03's evaluate is handed a demand count and a supply count at quote time and keeps none of the adjustment it returns, and the policy behind it is addressable only by a policyId no operation lists. The manual overrides are a floor on this number, not the number (Q-246).";

export function tileRow(tiles: SurgeTiles): readonly SurgeTile[] {
  return [
    {
      label: "Surge zones",
      value: tiles.surgeZones,
      classKey: "unknown",
      note:
        tiles.highestManualMultiplier === undefined
          ? "no manual surge"
          : `max ${multiplier(tiles.highestManualMultiplier)} by hand`,
      code: "PRC-03",
      status: "unknown",
      title: SURGE_ZONES_ABSENT,
    },
    {
      label: "Balanced",
      value: tiles.balanced,
      classKey: "done",
      note: "—",
      code: "DSP-06",
      status: "balanced",
    },
    {
      label: "Undersupplied",
      value: tiles.undersupplied,
      classKey: "failed",
      note: "—",
      code: "DSP-06",
      status: "undersupplied",
    },
    {
      label: "Manual overrides",
      value: tiles.manualOverrides,
      classKey: "human",
      note: tiles.soonestExpiry === undefined ? "—" : `expires ${clock(tiles.soonestExpiry)}`,
      code: "PRC-03",
      status: "override",
    },
  ];
}
